import type { JobRun } from '@/api/types'

export type JobRunState = 'running' | 'never_run' | 'failed' | 'succeeded'

export const JOB_RUN_STATE_LABELS: Record<JobRunState, string> = {
  running: 'Körs',
  never_run: 'Ej körd',
  failed: 'Misslyckades',
  succeeded: 'Lyckades'
}

export const JOB_RUN_STATE_STYLES: Record<JobRunState, string> = {
  running: 'bg-blue-50 text-blue-700 border-blue-200',
  never_run: 'bg-muted text-muted-foreground border-border',
  failed: 'bg-red-50 text-red-700 border-red-200',
  succeeded: 'bg-emerald-50 text-emerald-700 border-emerald-200'
}

export function getJobRunState(isRunning: boolean, run: JobRun | null): JobRunState {
  if (isRunning) {
    return 'running'
  }

  if (run === null) {
    return 'never_run'
  }

  if (run.error !== null) {
    return 'failed'
  }

  return 'succeeded'
}

export interface JobResultCount {
  key: string
  label: string
  value: number
  isProblem: boolean
}

const BILLING_RESULT_LABELS: Record<string, string> = {
  created: 'Skapade',
  registered: 'Registrerade',
  voided: 'Makulerade',
  failed: 'Misslyckade'
}

const SYNC_RESULT_LABELS: Record<string, string> = {
  checked: 'Kontrollerade',
  updated: 'Uppdaterade',
  pending: 'Väntar på import',
  rejected: 'Avvisade',
  failed: 'Misslyckade'
}

const SYNC_REQUEST_RESULT_LABELS: Record<string, string> = {
  invoices: 'Skickade för synk'
}

const SHIPMENT_MOTOR_RESULT_LABELS: Record<string, string> = {
  created: 'Skapade',
  booked: 'Bokade',
  failed: 'Misslyckade'
}

const SHIPMENT_TRACKING_RESULT_LABELS: Record<string, string> = {
  checked: 'Spårade',
  dispatched: 'Inlämnade',
  delivered: 'Levererade',
  returned: 'Returnerade',
  terminated: 'Avslutade abonnemang'
}

const RESULT_SHAPES: Record<string, string>[] = [
  BILLING_RESULT_LABELS,
  SYNC_RESULT_LABELS,
  SYNC_REQUEST_RESULT_LABELS,
  SHIPMENT_MOTOR_RESULT_LABELS,
  SHIPMENT_TRACKING_RESULT_LABELS
]

const PROBLEM_KEYS = ['failed', 'rejected']

const SCHEDULE_SOURCE_LABEL = 'Schemalagd'
const MANUAL_SOURCE_LABEL = 'Manuell'

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

function readCounts(result: Record<string, unknown>, labels: Record<string, string>): JobResultCount[] | null {
  const counts: JobResultCount[] = []

  for (const [key, label] of Object.entries(labels)) {
    const value = result[key]

    if (typeof value !== 'number') {
      return null
    }

    counts.push({ key, label, value, isProblem: PROBLEM_KEYS.includes(key) && value > 0 })
  }

  return counts
}

export function describeJobResult(result: unknown): JobResultCount[] {
  if (!isRecord(result)) {
    return []
  }

  for (const labels of RESULT_SHAPES) {
    const counts = readCounts(result, labels)

    if (counts !== null) {
      return counts
    }
  }

  return []
}

export function isSyncRequestResult(result: unknown): boolean {
  return isRecord(result) && typeof result.correlationId === 'string'
}

export function formatJobRunSource(run: JobRun): string {
  if (run.source === 'schedule') {
    return SCHEDULE_SOURCE_LABEL
  }

  return run.triggered_by_name ?? MANUAL_SOURCE_LABEL
}
